import { Input } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ChatCompletionRequestMessage } from 'openai';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, { Controls, MiniMap, addEdge, Background, Connection, Edge, Node, NodeTypes, OnSelectionChangeParams, ReactFlowInstance, SelectionMode, useEdgesState, useNodesState, XYPosition, useViewport, NodeChange, applyNodeChanges, EdgeChange, applyEdgeChanges } from 'reactflow';
import { getChatGPTResponseStream } from '../../../api/openai-api';

import loadingDots from "../../../assets/loading.gif";
import { ReactComponent as SendIcon } from '../../../assets/node-icons/send.svg';
import './baseline-flow.scss';
import { ResponseState } from '../../components/input.model';
import { MemoNodeData } from '../../nodes/memo-node/memo-node.model';
import MemoNode from "../../nodes/memo-node/memo-node";
import { highlightChatSelection, isHighlightable } from '../../triggers/highlighter/highlighter';
import { uuid } from '../../utils';
import { TopicNodeData, TypeTopicNode } from '../../nodes/topic-node/topic-node.model';
import { InstanceState } from '../../triggers/semantic-dive/semantic-dive';
import { CreativeNode } from '../../nodes/node.model';
import TopicNode from '../../nodes/topic-node/topic-node';

import SelectedTopicsToolbar from '../../components/selected-topics-toolbar/selected-topics-toolbar';
import { global_warming, example } from "../../user-study/script";
import { FlowProps } from '../../App';
import { initialBaselineNodes } from './baseline-initial-elements';

const proOptions = { account: "paid-pro", hideAttribution: true };

const panOnDrag = [1, 2];

const nodeTypes: NodeTypes = {
  memo: MemoNode,
  topic: TopicNode,
};

const minimapStyle = {
  height: 120,
};

const initialEdges: Edge[] = [];

const BaselineFlow = (props: FlowProps) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  const [nodes, setNodes] = useState(initialBaselineNodes(props.task));
  const [edges, setEdges] = useState(initialEdges);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Whenever a node is deleted, all children should not have that parentNode
      const removedNodeIds: string = changes
        .filter(change => change.type === 'remove')
        .map((change: any) => change.id)
        .join(','); // id do not have comma, so searching with includes() is just a hack

      let nodeIdsToRemove: { id: string }[] = [];

      setNodes((nodes) => applyNodeChanges(changes, nodes.map(node => {
        // Remove the parentNode if node is a child of a removed
        if (removedNodeIds && node.parentNode?.includes(removedNodeIds)) {
          const parent = reactFlowInstance?.getNode(node.parentNode);
          node.position.x += (parent?.position.x ?? 0) - (parent?.width ?? 0)/2;
          node.position.y += parent?.position.y ?? 0;
          node.parentNode = undefined;
        }
        return node;
      })));

      reactFlowInstance?.deleteElements({nodes: nodeIdsToRemove});
    },
    [reactFlowInstance, setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const [zoomRange, setZoomRange] = useState({min: 0.3, max: 3});

  const [chatInput, setChatInput] = useState('');
  const [response, setResponse] = useState('');
  const [responseInputState, setResponseInputState] = useState(ResponseState.INPUT);
  const [chatMessages, setChatMessages] = useState<ChatCompletionRequestMessage[]>([
    // {
    //   role: 'system',
    //   content: "For the sake of this conversation, you are Bryan. So whenever someone talks to you and asks you a question about you, answer as if you're Bryan. Bryan is a UCSD CS student doing research in HCI. He's about to graduate and is seeking a software engineering job. For fun, he plays tennis and takes photos, he's actually really good at both: played varsity and somewhat competitively, and received awards for his photos. His personality is a little shy, but they way he chats is more laid back and uses 'kinda' and 'pretty <adjective>' a lot. Whenever you speak, talk like you are relaxed a bit, but have low self esteem. that is what Bryan talks like. He's south korean american, grew up in seoul, but was born in Berkeley. He moved to seoul when he was five, and attended Yongsan International School of Seoul (YISS), until he moved back to California for highschool, where he attended San Domenico for his four years of high school."
    // }
  ]);
  const { x, y, zoom } = useViewport();

  // useEffect(() => {
  //   console.log(x, y, zoom);
  // }, [x, y, zoom]);

  useEffect(() => {
    const messagesElement = document.getElementById('highlight-box');
    if (messagesElement) {
      messagesElement.scrollTo({
        top: messagesElement.scrollHeight,
      })
    }
  }, [response]);

  const onConnect = useCallback(
    (params: Edge | Connection) =>
      reactFlowInstance?.setEdges((els) => addEdge(params, els)),
    [reactFlowInstance]
  );

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper?.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData("dragNodeType");

      // check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return;
      }

      const data_ = JSON.parse(event.dataTransfer.getData("dragNodeData"));
      const highlightedText = data_.label;

      if (reactFlowInstance && reactFlowBounds) {
        const position: XYPosition = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        const nodeId = `${type}-${uuid()}`;

        let data = {
          parentId: '',
          originalId: nodeId,
          chatHistory: [],
          instanceState: InstanceState.NONE,
          state: {
            topic: highlightedText,
            toolbarAvailable: false,
            isEditing: false,
            hasTopic: true,
            hasFourHandles: false,
          },
        } as TopicNodeData;

        const newNode: CreativeNode = {
          id: nodeId,
          dragHandle: ".drag-handle",
          type: 'topic',
          position,
          data: data as any,
        };

        reactFlowInstance.setNodes((nodes) =>
          nodes.concat(
            newNode
          ));
      }
    },
    [reactFlowInstance]
  );

  const onNodeDrag = useCallback(
    (_: any, node: Node) => {
      if (node.type !== "node" && !node.parentNode) {
        return;
      }

      if (reactFlowInstance) {
        const intersections = reactFlowInstance
          .getIntersectingNodes(node)
          .filter((n) => n.type === "group");
        const groupClassName =
          intersections.length && node.parentNode !== intersections[0]?.id
            ? "active"
            : "";

        reactFlowInstance.setNodes((nds) => {
          return nds.map((n) => {
            if (n.type === "group") {
              return {
                ...n,
                className: groupClassName,
              };
            } else if (n.id === node.id) {
              return {
                ...n,
                position: node.position,
              };
            }

            return { ...n };
          });
        });
      }
    },
    [reactFlowInstance]
  );

  const onSelectionChange = useCallback(
    (params: OnSelectionChangeParams) => {
      // const selectedTopicNodes: TypeTopicNode[] = params.nodes.filter(
      //   (node) => node.type === "topic" && node.selected && !node.parentNode
      // );
    },
    [reactFlowInstance]
  );

  const handleInputChange = (event: any) => {
    setChatInput(event.target.value);
  }

  const handleKeyPress = async (event: any) => {
    if(event.key === 'Enter' && !event.shiftKey){
      submitPrompt(event.target);
    }
  };

  const submitPrompt = (inputElement: HTMLElement | null) => {
    inputElement?.blur();
    setChatInput('');
    generateResponse(chatInput).then((hasCompleted) => {
      inputElement?.focus();
    });
  }

  const generateResponse = useCallback(async (prompt: string) => {

    if (!prompt || responseInputState === ResponseState.LOADING) return;
    setResponseInputState(ResponseState.LOADING);

    // Add user's prompt to chat list
    chatMessages.push({
      role: 'user',
      content: prompt,
    },
    {
      role: 'assistant',
      content: '',
    });
    setChatMessages(chatMessages);

    // Stream text
    return getChatGPTResponseStream(chatMessages.slice(0, -2), prompt, (text: string) => {
      // save to response and update text in node
      setResponse(text);
      chatMessages.at(-1)!.content = text;
      setChatMessages(chatMessages);
    }).then(data => {
      setResponseInputState(ResponseState.INPUT);
      setResponse('');
      return true;
    }).catch(() => {
      return false;
    });
  }, [chatMessages, response]);



  // add memo node when user double clicks on canvas
  const onPaneClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      if (reactFlowInstance) {
        const reactFlowBounds = reactFlowWrapper?.current?.getBoundingClientRect();
        const position: XYPosition = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds!.left,
          y: event.clientY - reactFlowBounds!.top,
        });

        if (event.detail === 1) { // Single click, drop semantic carry list if exists
          return;
        } else if ((event.altKey || event.metaKey || event.shiftKey) && event.detail === 2) {
            // alt or command or shift + double click to create memo node
            const data: MemoNodeData = {
              state: {},
            };

            const nodeId = `flex-${uuid()}`;
            reactFlowInstance.addNodes([
              {
                id: nodeId,
                type: 'memo',
                dragHandle: '.drag-handle',
                position,
                data,
              },
            ]);
        } else if (event.detail === 2) {
          // create default node 

          const baselineNodeId = `baseline-${uuid()}`;

          let data = {
            parentId: '',
            originalId: baselineNodeId,
            chatHistory: [],
            instanceState: InstanceState.NONE,
            state: {
              topic: '',
              toolbarAvailable: false,
              hasTopic: false,
              hasFourHandles: true,
            },
          } as TopicNodeData;

          const newNode: CreativeNode = {
            id: baselineNodeId,
            dragHandle: ".drag-handle",
            type: 'topic',
            position,
            data: data as any,
          };

          reactFlowInstance.setNodes((nodes) =>
            nodes.concat(
              newNode
            ));

            setTimeout(() => {
              const currElement = document.querySelectorAll(`[data-id="${baselineNodeId}"]`)[0];
              const inputElement = currElement.getElementsByClassName('text-input')[0] as HTMLInputElement;
              inputElement?.focus();
            }, 100);

        }
      }
    },
    [reactFlowInstance]
  );



  return (
    <div className='baseline-flow'>
      <div className='chat-panel'>
        <div
          className='chat-messages'
          id='highlight-box'
          onMouseUp={() => highlightChatSelection(reactFlowInstance as ReactFlowInstance<any, any>, reactFlowWrapper)}
        >
          {
            chatMessages
            .filter(message => message.role !== 'system')
            .map((message, index) => (
              <div
                key={index+message.content}
                className={message.role === 'user' ? 'user-chat' : 'gpt-chat'}
              >
                <h5>{ message.role === 'user' ? 'You' : 'GPT' }</h5>
                <p>{ message.content }</p>
              </div>
            ))
          }
          <div className='chat-padding'></div>
        </div>
        <div className='text-input-box'>
            <form
              className='text-input'
              onSubmit={(event) => {
                submitPrompt(document.getElementById('input-text-area'));
                event.preventDefault();
              }}
            >
              <TextArea
                id='input-text-area'
                className='text-area'
                name='text'
                placeholder='Ask ChatGPT'
                autoComplete='off'
                value={chatInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                bordered={false}
                autoSize
              />
                {
                  responseInputState === ResponseState.INPUT ?
                  <button
                    onClick={() => submitPrompt(document.getElementById('input-text-area'))}
                    type="button"
                  >
                    <SendIcon />
                  </button> :
                  <img width="18px" height="18px" src={loadingDots} alt="loading..." />
                }
            </form>
        </div>
      </div>
      <div id="reactflow-wrapper" className="reactflow-wrapper" ref={reactFlowWrapper}>
        <ReactFlow
          id='reactFlowInstance'
          proOptions={proOptions}
          nodes={nodes}
          edges={edges}
          fitView
          selectionOnDrag
          zoomOnPinch
          panOnScroll
          panOnDrag={panOnDrag}
          zoomOnDoubleClick={false}
          selectionMode={SelectionMode.Partial}
          multiSelectionKeyCode={'Shift'}
          nodeOrigin={[0.5, 0]}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          // onNodeClick={onNodeClick}
          onSelectionChange={onSelectionChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          // edgeTypes={edgeTypes}
          onNodeDrag={onNodeDrag}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onPaneClick={onPaneClick}
          maxZoom={zoomRange.max}
          minZoom={zoomRange.min}
          elevateNodesOnSelect
        >
          <MiniMap style={minimapStyle} zoomable pannable />
          <Controls />
          <Background />
        </ReactFlow>
        <SelectedTopicsToolbar />
      </div>
    </div>
  )
}

export default BaselineFlow;